/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Fragment, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';

import { type NLPSearchResultSource } from '../../../../ai-answer-dialog/types';
import { SourcesStack } from '../../../../common/ui/sources-stack/sources-stack';
import { useFetchSmartlinkData } from '../../../common/hooks/use-fetch-smartlink-data';

import { DefinitionSource } from './DefinitionSource';

interface DefinitionSourcesProps {
	sources: NLPSearchResultSource[];
}

const messages = defineMessages({
	definition_sources_stack_text: {
		id: 'search_ai.definition_sources_stack_text',
		defaultMessage:
			// eslint-disable-next-line @atlassian/i18n/enforce-plural-rules
			'{numResolvedSources, plural, =1 {{numResolvedSources, number} Source} other {{numResolvedSources, number} Sources}}',
		description:
			'Text shown next to a sources icon stack in the definition card, indicating the number of sources used. The placeholder {numResolvedSources} is substituted with the count of resolved source documents.',
	},
	definition_source_stack_text: {
		id: 'search_ai.definition_source_stack_text',
		defaultMessage: 'source',
		description:
			'Label shown next to a single source icon in the definition card sources section when only one source document is available.',
	},
});

const styles = cssMap({
	root: {
		paddingTop: token('space.100'),
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
		paddingBottom: token('space.100'),
	},
	sourcesStackContainerStyles: {
		width: 'fit-content',
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
	},
	dividerStyles: {
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
	},
});

const DEFINITION_MAX_SOURCES = 3;

export const DefinitionSources: ({ sources }: DefinitionSourcesProps) => JSX.Element | null = ({
	sources,
}: DefinitionSourcesProps) => {
	const [sourcesExpanded, setSourcesExpanded] = useState(false);
	const { formatMessage } = useIntl();

	const sourcesUrls = sources.map((source) => source.url).slice(0, DEFINITION_MAX_SOURCES);

	const { objectResolverData } = useFetchSmartlinkData({ urls: sourcesUrls });
	const resolvedSources = objectResolverData.flatMap(({ sourceData, sourceIndex }) => {
		const source = sources[sourceIndex];

		return source ? [{ source, sourceData, sourceIndex }] : [];
	});
	const numResolvedSources = resolvedSources.length;

	return numResolvedSources === 0 ? null : (
		<Box xcss={styles.root}>
			<Stack space="space.100" alignBlock="center">
				<Inline spread="space-between" alignBlock="center">
					<Button onClick={() => setSourcesExpanded(!sourcesExpanded)}>
						<Inline space="space.050" alignBlock="center">
							<SourcesStack
								maxSourcesInStack={DEFINITION_MAX_SOURCES}
								sources={resolvedSources.map(({ source }) => source)}
							/>
							<Text color="color.text.subtle">
								{formatMessage(messages.definition_sources_stack_text, {
									numResolvedSources,
								})}
							</Text>
						</Inline>
					</Button>
					<IconButton
						onClick={() => setSourcesExpanded(!sourcesExpanded)}
						icon={(iconProps) =>
							sourcesExpanded ? (
								<ChevronDownIcon {...iconProps} size="small" />
							) : (
								<ChevronRightIcon {...iconProps} size="small" />
							)
						}
						appearance="subtle"
						label={
							sourcesExpanded
								? expValEquals('a11y-fixes-week3-may-2026', 'isEnabled', true)
									? 'Collapse sources'
									: 'Collapse sources button'
								: expValEquals('a11y-fixes-week3-may-2026', 'isEnabled', true)
									? 'Expand sources'
									: 'Expand sources button'
						}
						testId="definitions-sources-expand"
					/>
				</Inline>
				{sourcesExpanded && (
					<Fragment>
						<Box xcss={styles.dividerStyles} />
						<Stack space="space.200">
							{resolvedSources.map(({ source, sourceData, sourceIndex }) => (
								<DefinitionSource
									source={source}
									sourceData={sourceData}
									sourceIndex={sourceIndex}
									key={`${sourceData.data.url}-${sourceIndex}`}
								/>
							))}
						</Stack>
					</Fragment>
				)}
			</Stack>
		</Box>
	);
};
